/**
 * Project Atlas — shared constants.
 *
 * Single source of truth for tick rate, AOI cell geometry, world sizing, and
 * movement/anti-cheat bounds. Imported by the EngineX client. The server
 * module mirrors the values it needs in `server/src/lib/constants.ts` (the
 * SpacetimeDB module bundles from its own root and cannot reach across the
 * monorepo boundary) — keep the two in sync; CLAUDE.md documents the rule.
 */

// ─── Simulation ────────────────────────────────────────────────────────────────

/** Fixed-sim rate. Movement math is deterministic at this rate on both sides. */
export const TICK_HZ = 60;
/** Fixed timestep in seconds (1/60). */
export const FIXED_DT = 1 / TICK_HZ;

// ─── Area of Interest (spatial subscriptions) ──────────────────────────────────

/** World-space size of one AOI cell, in meters. */
export const CELL_SIZE_M = 64;
/** Subscription radius (in cells) around the player. */
export const AOI_RADIUS_CELLS = 2;
/**
 * Manhattan distance (in cells) the player must cross before the client
 * re-subscribes. Prevents subscription thrashing at cell boundaries.
 */
export const AOI_HYSTERESIS_CELLS = 1;
/** Minimum fixed-sim ticks between AOI subscription recomputes. */
export const AOI_UPDATE_INTERVAL_TICKS = 6;

// ─── World topology ────────────────────────────────────────────────────────────

/**
 * Reserved logical id / platform sentinel for the Commons. SpacetimeDB
 * `world_id` is auto-increment, so the *live* Commons row id is resolved at
 * runtime by querying `world WHERE kind = 'commons'`; this constant marks the
 * platform-owned reservation and the "take me to the Commons" intent.
 */
export const COMMONS_WORLD_ID = 0n;
/** Every account is seeded one personal world of this edge length at signup. */
export const DEFAULT_PERSONAL_WORLD_SIZE_M = 64;
/** Display name of the shared Commons shard. */
export const COMMONS_NAME = 'The Commons';

/** Canonical world `kind` discriminators. Never rename (see CLAUDE.md glossary). */
export const WORLD_KIND = {
  Commons: 'commons',
  Personal: 'personal',
  Group: 'group',
  Arcade: 'arcade',
  /** Unlimited on-demand owner-sovereign instances (P14.4 freeform building). */
  Individual: 'individual',
  /** The Merchant Hive — platform-owned commerce planet. */
  Merchant: 'merchant',
} as const;

/** Canonical world `visibility` values. */
export const WORLD_VISIBILITY = {
  Public: 'public',
  Private: 'private',
  Unlisted: 'unlisted',
} as const;

/** Canonical world `lifecycle` values (tiers added in P4). */
export const WORLD_LIFECYCLE = {
  Persistent: 'persistent',
  Ephemeral: 'ephemeral',
  Seasonal: 'seasonal',
  Legacy: 'legacy',
} as const;

// ─── World Governance / The Forge (P4) ──────────────────────────────────────────
//
// The Permission Matrix, governance templates, authorship modes, jurisdiction
// layers, and lifecycle policy bounds. Mirrors `server/src/lib/constants.ts` —
// keep in sync. The server is authoritative for all gating; these power the
// client UI (template picker, role matrix, capacity slider, quarantine badge).

/** Governance templates an owner can stamp on a world. */
export const GOVERNANCE_TEMPLATE = {
  Solo: 'solo',
  Democracy: 'democracy',
  Anarchy: 'anarchy',
  ScheduledEvent: 'scheduled_event',
  Gallery: 'gallery',
  GameArena: 'game_arena',
} as const;

/** Authorship modes. Default is Solo — collaboration is strictly opt-in. */
export const AUTHORSHIP = {
  Solo: 'solo',
  Collaborative: 'collaborative',
  OpenSource: 'open_source',
  Guild: 'guild',
} as const;

/** Jurisdiction layers (who governs kicks/bans/rules/content). */
export const JURISDICTION = {
  OwnerSovereign: 'owner_sovereign',
  Platform: 'platform',
  Competitive: 'competitive',
} as const;

/** Roles in the Permission Matrix (most→least privileged). */
export const WORLD_ROLE = {
  Owner: 'owner',
  Admin: 'admin',
  Build: 'build',
  Script: 'script',
  Decorate: 'decorate',
  Visitor: 'visitor',
} as const;

/** Cloud-native instance capacity bounds (concurrent avatars). */
export const MIN_WORLD_CAPACITY = 1;
export const MAX_WORLD_CAPACITY = 50;
/** World edge-length clamp for `spawnWorld` (meters). */
export const MIN_WORLD_SIZE_M = 16;
export const MAX_WORLD_SIZE_M = 512;

/**
 * Commons sea level (m). Continental basins below this fill with the engine's
 * realistic water (waves shoal onto the beaches). Deliberately deep so the
 * origin city pin (macro = 0) and the wetland dips (≈ −8 m) stay dry; the
 * client additionally clamps below the lowest seeded settlement site
 * (`commonsSeaLevel`) so no settlement ever drowns. Rendering-only for now —
 * the server does not yet gate movement/placement against water.
 */
export const SEA_LEVEL_M = -40;
/** Collaborative authorship co-author cap (owner is the first author). */
export const MAX_CO_AUTHORS = 5;
/** Open-source remix royalty in basis points (1000 = 10%) — for UI display. */
export const OPEN_SOURCE_ROYALTY_BPS = 1000;
/** Verified ordinary reports that trip an owner-sovereign world into quarantine. */
export const QUARANTINE_REPORT_THRESHOLD = 3;
/** Legacy eligibility visitor threshold (status + delete-protection). */
export const LEGACY_MIN_VISITORS = 100;

// ─── Movement & server-side anti-cheat ─────────────────────────────────────────

/** Default walk speed (m/s). */
export const MOVE_SPEED_MPS = 6;
/** Sprint speed (m/s). */
export const SPRINT_SPEED_MPS = 10;
/**
 * Hard server cap on horizontal speed (m/s). The `move` reducer rejects any
 * displacement implying a speed above this — the client is never trusted.
 * Set above SPRINT with margin to tolerate jitter without false positives.
 */
export const MAX_MOVE_SPEED_MPS = 12;
/**
 * Single-tick displacement (m) beyond which a `move` is treated as a teleport
 * attempt and clamped/rejected by the server.
 */
export const TELEPORT_THRESHOLD_M = 5;

/** Default spawn position in any freshly entered world. */
export const SPAWN_POS = { x: 0, y: 1, z: 0 } as const;

// ─── Connection ────────────────────────────────────────────────────────────────

/** Self-hosted SpacetimeDB endpoint (Docker or native `spacetime start`). */
export const STDB_URI = 'http://localhost:3000';
/** Published database name. */
export const STDB_DB_NAME = 'atlas';

// ─── Economy (P3) ───────────────────────────────────────────────────────────────
//
// Dual currency. SOV (Sovereigns) is the hard, dev-controlled currency; INF
// (Influence) is the soft, socially-earned one. Both are integer (i64/bigint) on
// the wire — one whole coin is the indivisible base unit. The server mirrors the
// money-math constants as basis-points in `server/src/lib/constants.ts`; these
// floats are for client display/estimation only. Keep the two in sync.

/** Currency codes. */
export const CURRENCY = {
  /** Sovereigns — hard currency, dev-controlled faucets/sinks. */
  Sovereigns: 'SOV',
  /** Influence — CREDITS UNIFICATION (2026-08-09): an alias of SOV. The server
   *  mirrors `INF = SOV`; a ledger leg naming the literal 'INF' is rejected
   *  and `wallet.inf` is never written — read `wallet.sov`. */
  Influence: 'SOV',
} as const;
export type CurrencyCode = (typeof CURRENCY)[keyof typeof CURRENCY];

/** Marketplace tax withheld from seller proceeds (display). */
export const MARKET_TAX = 0.05;
/** Travel/arcade ticket fee (display; reserved). */
export const TICKET_FEE = 0.03;
/** Creator Exchange platform cut (display; reserved for P10). */
export const PLATFORM_CUT = 0.15;
/** Group/guild formation SOV sink (reserved). */
export const GROUP_FORMATION_SOV = 1_000n;
/** One-time new-player SOV faucet amount. */
export const NEW_PLAYER_GRANT_SOV = 1_000n;

/** Upload SOV sink components (for client cost estimation before upload). */
export const UPLOAD_BASE_SOV = 10n;
export const UPLOAD_SOV_PER_1K_POLYS = 1n;
export const UPLOAD_SOV_PER_MB = 5n;
export const BYTES_PER_MB = 1_048_576n;

/** Escrow auto-release window: 48 hours in milliseconds (client-side display). */
export const ESCROW_WINDOW_MS = 48 * 60 * 60 * 1000;

/** Canonical escrow `state` values (mirror of the server enum). */
export const ESCROW_STATE = {
  Held: 'held',
  Released: 'released',
  ChargedBack: 'charged_back',
} as const;

// ─── World Logic & Behavioral Contracts (P5) ────────────────────────────────────
//
// Node-based visual scripting compiles to a sandboxed Lua Behavioral Contract.
// The server is authoritative for EVERY capability/budget decision (it runs the
// compiled op-IR in a deterministic interpreter — see server/src/lib/sandbox.ts);
// these constants power the client node editor, capability-request UI, and the
// throttle badge. Mirrors `server/src/lib/constants.ts` — keep in sync.

/**
 * Contract lifecycle state. Over-budget contracts are THROTTLED, never banned —
 * a throttled contract keeps running at a reduced tick cadence and the owner is
 * told why (mechanical pollution control, not censorship). `disabled` is an
 * explicit owner/platform stop, distinct from automatic throttling.
 */
export const CONTRACT_STATE = {
  Active: 'active',
  Throttled: 'throttled',
  Disabled: 'disabled',
} as const;
export type ContractStateName = (typeof CONTRACT_STATE)[keyof typeof CONTRACT_STATE];

/**
 * The capability vocabulary a contract may REQUEST and a world owner may GRANT.
 * A capability NEVER crosses the owning world's boundary — `objects.*` only ever
 * touch objects in the contract's own `world_id`. Forward-declared caps
 * (`tickets.issue` P8, `player.pulse.read` P6, `economy.tip` P10) are recorded as
 * intent only in P5: the contract may emit the event, but settlement is owned by
 * the later phase. No SOV/INF moves through a P5 contract.
 */
export const CONTRACT_CAP = {
  /** Read object transforms/state within THIS world. */
  ObjectsRead: 'objects.read',
  /** Mutate object transforms/state within THIS world. */
  ObjectsWrite: 'objects.write',
  /** Emit a world-level event other contracts / the world may react to. */
  WorldEventsEmit: 'world.events.emit',
  /** Record a ticket-issue intent for the P8 event/ticketing system (no settlement here). */
  TicketsIssue: 'tickets.issue',
  /** Read the player's aggregate Pulse signal (P6; gated, intent-only here). */
  PlayerPulseRead: 'player.pulse.read',
  /** Record a tip intent for the P10 economy (no SOV movement here). */
  EconomyTip: 'economy.tip',
} as const;
export type ContractCapName = (typeof CONTRACT_CAP)[keyof typeof CONTRACT_CAP];

/** Every capability name, for client validation of a requested set. */
export const CONTRACT_CAPS: readonly ContractCapName[] = [
  CONTRACT_CAP.ObjectsRead,
  CONTRACT_CAP.ObjectsWrite,
  CONTRACT_CAP.WorldEventsEmit,
  CONTRACT_CAP.TicketsIssue,
  CONTRACT_CAP.PlayerPulseRead,
  CONTRACT_CAP.EconomyTip,
];

/**
 * Default per-tick execution budget. CPU is a deterministic STEP count (one IR
 * op = one step — wall-clock is non-deterministic and unavailable in a reducer),
 * surfaced to the author as an approximate "ms" figure. Memory is the byte size
 * of the contract's persisted key/value state.
 */
export const CONTRACT_CPU_BUDGET_MS = 8;
export const CONTRACT_MEM_BUDGET_KB = 64;
/** Hard ceilings an owner may raise a budget to (anti-DoS even for owners). */
export const CONTRACT_MAX_CPU_BUDGET_MS = 64;
export const CONTRACT_MAX_MEM_BUDGET_KB = 512;
/** Deterministic steps-per-"ms" used to turn the cpu budget into a step ceiling. */
export const CONTRACT_STEPS_PER_MS = 2000;
/**
 * Cadence multiplier applied while a contract is throttled — it still runs (never
 * banned), just 1/Nth as often. Surfaced in the owner's throttle badge.
 */
export const CONTRACT_THROTTLE_FACTOR = 8;

/** The four shipped starter graphs in the node-editor template library. */
export const CONTRACT_TEMPLATE = {
  Door: 'door',
  Trivia: 'trivia',
  Timer: 'timer',
  Spawner: 'spawner',
} as const;
export type ContractTemplateName = (typeof CONTRACT_TEMPLATE)[keyof typeof CONTRACT_TEMPLATE];

// ─── Social Pulse / Compass / Orbits (P6) ────────────────────────────────────────
//
// Mirrored verbatim in `server/src/lib/constants.ts` (the authoritative copy —
// the SpacetimeDB module bundles from its own root and cannot import this file).
// Keep the two in sync; CLAUDE.md documents the rule.
//
// HARD INVARIANT: the Social Pulse is NEVER punitive. A low or zero need grants
// NO debuff and locks NO content — needs are *invitations*, never *chores*. The
// client reflects the authoritative gauges; it never computes or penalizes them.

/** The four avatar needs. Each is a 0..100 gauge that decays over real time. */
export const NEED = {
  Social: 'social',
  Fun: 'fun',
  Creativity: 'creativity',
  Rest: 'rest',
} as const;
export type NeedName = (typeof NEED)[keyof typeof NEED];
/** Every need name, for client pickers / validation. */
export const NEEDS: readonly NeedName[] = [NEED.Social, NEED.Fun, NEED.Creativity, NEED.Rest];

/** Every Pulse need starts here at account birth. */
export const PULSE_START = 50;
/** Pulse gauges are clamped to this inclusive range. */
export const PULSE_MIN = 0;
export const PULSE_MAX = 100;
/** Baseline decay applied to every need, points per real minute (tuning hook). */
export const PULSE_DECAY_PER_MIN = 1;
/** Microseconds in one real minute. */
export const MICROS_PER_MIN = 60_000_000n;

/** A mutual Wave grants this many Social points to BOTH avatars (the "+5%"). */
export const WAVE_SOCIAL_BOOST = 5;
/** Compasses are only legible — and Waves only land — within this radius (m). */
export const WAVE_RANGE_M = 10;
/** Window (µs) in which a return Wave counts as mutual (5 min). */
export const WAVE_MUTUAL_WINDOW_MICROS = 300_000_000n;

/** Minimum interval (µs = 30 min) between Compass rotations unless a mood-shift. */
export const COMPASS_ROTATE_COOLDOWN_MICROS = 1_800_000_000n;
/** A public Compass is only shown to strangers within this radius (m). */
export const COMPASS_VISIBILITY_RANGE_M = 10;

/** Players within this radius (m) in the same world cluster into one Orbit. */
export const ORBIT_RANGE_M = 10;
/** An Orbit with no validated interaction for this long (µs = 4 h) is expired. */
export const ORBIT_IDLE_EXPIRY_MICROS = 14_400_000_000n;

/** Flat once-per-day Social Pulse SOV faucet (tuning hook; once/day, no streak). */
export const DAILY_PULSE_REWARD_SOV = 100n;
/** Microseconds in one UTC day; the day bucket is `floor(micros / this)`. */
export const DAY_BUCKET_MICROS = 86_400_000_000n;

/**
 * Want-affinity groups. Two Wants are compatible (the gold ice-break highlight)
 * iff they share a group. Fears are free-text and never gate a Wave.
 */
export const WANT_GROUP: Readonly<Record<string, readonly string[]>> = {
  conversation: ['deep_conversation', 'listen', 'share_story'],
  create: ['collaborate', 'build_together', 'make_art'],
  adventure: ['explore', 'discover', 'wander'],
  play: ['play', 'compete', 'goof_off'],
  relax: ['relax', 'chill', 'rest_together'],
};
/** Flat list of every recognized Want (for client pickers / validation). */
export const WANTS: readonly string[] = Object.values(WANT_GROUP).flat();

/** The affinity group a Want belongs to, or null if it is not a recognized Want. */
export function wantGroupOf(want: string): string | null {
  for (const [group, members] of Object.entries(WANT_GROUP)) {
    if (members.includes(want)) return group;
  }
  return null;
}

/**
 * Two Wants are compatible (the gold ice-break highlight, and the only Wants a
 * Wave may bridge) iff they belong to the same affinity group. An unrecognized
 * Want is compatible with nothing. Mirrors `server/src/lib/social.ts` — the
 * client only ever uses this to *suggest* a Wave; the server re-checks and is
 * the authority on whether a Wave actually lands.
 */
export function wantsCompatible(a: string, b: string): boolean {
  const ga = wantGroupOf(a);
  if (ga === null) return false;
  return ga === wantGroupOf(b);
}

// ─── Reputation & Trust (P7) ─────────────────────────────────────────────────────
//
// Mirrored verbatim in `server/src/lib/constants.ts` (the authoritative copy —
// the SpacetimeDB module bundles from its own root and cannot import this file).
// Keep the two in sync; CLAUDE.md documents the rule.
//
// HARD INVARIANTS:
//   • Reputation is SERVER-COMPUTED and NEVER client-set. The rollup scores
//     (Social Capital, Creator Rating, Notoriety) are recomputed by the hourly
//     batch; discrete penalties (the betrayal −30 / Traitor tag / voucher
//     cascade) are applied immediately by `recordBetrayal`. There is no reducer
//     that takes a score as an argument, so a client can never write one.
//   • Vouching stakes REAL, NON-TRANSFERABLE Influence. If the vouchee is later
//     recorded as a betrayer, EVERY active voucher takes the cascade hit — there
//     is no way to escape it after the fact.
//   • Honest and Caution reviews are first-class. Review-bombing is contained by
//     WEIGHTING (one active review per reviewer), never by hiding negatives.

/** Trustworthiness is a 0..100 gauge; a fresh account starts neutral. */
export const TRUST_NEUTRAL_START = 50;
export const TRUST_MIN = 0;
export const TRUST_MAX = 100;

/** Creator-rating stars are clamped to this inclusive range. */
export const REVIEW_MIN_STARS = 1;
export const REVIEW_MAX_STARS = 5;

/** A vouch must stake at least this much Influence (the minimum bond). */
export const VOUCH_MIN_INF_STAKE = 1n;
/**
 * Influence balance at/above which a voucher's INF term saturates (full weight).
 * Below it, the vouch counts proportionally less — low-Influence vouches are
 * structurally down-weighted, so a freshly-minted alt cannot lend real weight.
 */
export const VOUCH_WEIGHT_INF_FULL = 100n;
/** A vouch's weight (×100) is clamped to this band: 0.10× … 3.00×. */
export const VOUCH_WEIGHT_MIN_X100 = 10;
export const VOUCH_WEIGHT_MAX_X100 = 300;

/** A proven collaboration betrayal drops the betrayer's Trustworthiness by this. */
export const BETRAYAL_TRUST_PENALTY = 30;
/** The Traitor tag is visible for 30 days (microseconds). */
export const TRAITOR_TAG_MICROS = 2_592_000_000_000n;
/**
 * The "betrayal premium": betraying a collaborator nets 2× the normal asset
 * value from the interaction. This is the design rationale recorded with every
 * `betrayal_record` — the math is tempting precisely so the SOCIAL cost (a
 * 30-day Traitor tag, an 80% collapse in future collaboration, a −30
 * Trustworthiness hit, and the voucher cascade) can dominate it.
 */
export const BETRAYAL_PREMIUM_MULT = 2;

/** A betrayer's active vouchers each lose 10% Trustworthiness (basis points). */
export const VOUCHER_TRUST_PENALTY_BPS = 1_000n;
/** …and 5% of their liquid Influence (basis points). */
export const VOUCHER_INF_PENALTY_BPS = 500n;

/**
 * While the Traitor tag is active, collaboration eligibility is cut by 80%
 * (basis points). This is the headline "reduce all future collaboration
 * requests by 80%" — attributable to the tag alone, independent of the score.
 */
export const COLLAB_TRAITOR_REDUCTION_BPS = 8_000n;
/** Each Caution review further multiplies collaboration eligibility by 0.80. */
export const CAUTION_REVIEW_COLLAB_REDUCTION_BPS = 2_000n;
/** At most this many Caution reviews compound into the eligibility multiplier. */
export const MAX_CAUTION_PENALTIES = 3;
/** Collaboration eligibility never drops below this floor (×100) — never zero. */
export const COLLAB_ELIGIBILITY_FLOOR_X100 = 5;
/** Full (un-penalized) collaboration eligibility (×100 = 100%). */
export const COLLAB_ELIGIBILITY_FULL_X100 = 100;

/**
 * Documented hourly cadence of `recomputeReputationHourly` (microseconds). The
 * codebase ships no scheduler (like the P6 client-driven Pulse decay), so an
 * external cron drives the cadence post-MVP; the reducer itself is a pure,
 * idempotent rollup that is safe to call at any time.
 */
export const REPUTATION_RECOMPUTE_INTERVAL_MICROS = 3_600_000_000n;

// ─── Rituals, Events & the Memory Book (P8) ──────────────────────────────────────
//
// Mirrored verbatim in `server/src/lib/constants.ts` (the authoritative copy —
// the SpacetimeDB module bundles from its own root and cannot import this file).
// Keep the two in sync; CLAUDE.md documents the rule.
//
// HARD INVARIANTS:
//   • Ticket money settles ONLY through the P3 escrow primitive: SOV is held for
//     ESCROW_WINDOW_MICROS (48h) with a TICKET_FEE_BPS (3%) processing-fee SINK,
//     and a chargeback DEBITS THE SELLER. No ticket SOV ever moves outside the
//     ledger/escrow path, and the 3% fee is non-refundable.
//   • A recurring event institutionalizes into a ritual only after
//     RITUAL_INSTITUTIONALIZE_REPS convened sessions; the bound NPC host is
//     DETERMINISTIC (no LLM at launch) and remembers regulars in structured JSON.
//   • Standing Invitations are NOT spam: sent only for rituals with
//     RITUAL_MIN_HUMANS_LAST_SESSION+ human attendees last session, NEVER to a
//     muted player, and with no streaks or guilt — a held seat, not a chore.
//   • The Memory Book is the FIRST production Influence faucet (hosts + curators
//     earn INF). INF stays non-transferable: it can be EARNED but never bought
//     with SOV — there is no SOV→INF path anywhere in the ledger.

/** Canonical event recurrence values. '' = a one-off (no ritual row is created). */
export const RECURRENCE = {
  None: '',
  Daily: 'daily',
  Weekly: 'weekly',
} as const;
export type RecurrenceName = (typeof RECURRENCE)[keyof typeof RECURRENCE];
/** Every accepted recurrence string (for `createEvent` validation). */
export const RECURRENCES: readonly string[] = [RECURRENCE.None, RECURRENCE.Daily, RECURRENCE.Weekly];

/**
 * A recurring event institutionalizes into a ritual after this many CONVENED
 * sessions (a new session = a check-in in a later day-bucket than the last). The
 * prompt fixes this at "3 repetitions".
 */
export const RITUAL_INSTITUTIONALIZE_REPS = 3;
/**
 * Attending at least this many sessions makes an avatar a "regular" the NPC host
 * remembers and saves a seat for. < the institutionalize threshold so a host has
 * regulars to greet the moment it is bound.
 */
export const RITUAL_REGULAR_MIN_SESSIONS = 2;
/**
 * Anti-spam gate: a Standing Invitation is only sent for a ritual that drew at
 * least this many DISTINCT HUMAN attendees in its most recent session. A ritual
 * that has dwindled below a real crowd goes quiet rather than nagging.
 */
export const RITUAL_MIN_HUMANS_LAST_SESSION = 3;
/**
 * A regular who has not been seen for at least this long (µs = 3 days) is
 * eligible for a Standing Invitation ("we missed you; your seat is saved").
 */
export const STANDING_INVITE_LAPSE_MICROS = 259_200_000_000n;
/**
 * Social Pulse points granted for attending an institutionalized ritual — the
 * "Cheers" boost (everybody-knows-your-name), deliberately larger than a 5-pt
 * Wave so showing up to your ritual is the strongest social satisfier. Never a
 * quest: missing it costs nothing.
 */
export const RITUAL_SOCIAL_BOOST = 15;

/** Average event satisfaction (0..100) at/above which the host earns Influence. */
export const EVENT_SATISFACTION_INF_THRESHOLD = 80;
/** Influence minted to a ritual host when an event clears the satisfaction bar (once per event). */
export const RITUAL_HOST_INF_REWARD = 10n;
/** SOV faucet paid to a host when an event meets its attendance target (once per event). */
export const EVENT_HOST_BONUS_SOV = 200n;
/** Influence minted to a Memory Book contributor for a curated entry. */
export const MEMORY_CONTRIB_INF_REWARD = 1n;

/** An event with at least this many lifetime attendees earns a PERMANENT Commons memorial. */
export const MEMORIAL_MIN_ATTENDEES = 100;
/**
 * An archived ephemeral world is preserved as a read-only "ghost" in the Memory
 * Book for this long (µs = 90 days) before its entry expires.
 */
export const GHOST_WORLD_TTL_MICROS = 7_776_000_000_000n;

/** Deterministic NPC host archetypes. The LLM-backed "NPC Confidant" is post-launch. */
export const NPC_HOST_KIND = {
  RitualHost: 'ritual_host',
} as const;

/**
 * Canonical Memory Book entry kinds — the searchable archive's row discriminator.
 * `group_history` is STORED in P8 but AUTHORED by P9 (the kind is reserved now).
 */
export const MEMORY_ENTRY_KIND = {
  EventPhoto: 'event_photo',
  Replay: 'replay',
  Milestone: 'milestone',
  Lineage: 'lineage',
  GhostWorld: 'ghost_world',
  Memorial: 'memorial',
  GroupHistory: 'group_history',
} as const;
export type MemoryEntryKindName = (typeof MEMORY_ENTRY_KIND)[keyof typeof MEMORY_ENTRY_KIND];
/** Every valid Memory Book entry kind (for `addMemoryEntry` validation). */
export const MEMORY_ENTRY_KINDS: readonly string[] = Object.values(MEMORY_ENTRY_KIND);

// ─── Multi-Biome Commons Terrain (Phase A) ────────────────────────────────────────
//
// Mirrored verbatim in `server/src/lib/constants.ts`. The city-pin ensures the
// central Commons is always regolith_plains (the "safe" buildable biome); outside
// the falloff the six procedural biomes blend freely from the climate/ruggedness
// noise fields. Values are TUNABLE starting points — see `shared/terrain.ts`.

/** Radius (m) from origin within which weights collapse to 100% regolith_plains. */
export const CITY_PIN_RADIUS = 600;
/** Outer edge of the pin falloff (m); between CITY_PIN_RADIUS and this the blend is smoothstepped. */
export const CITY_PIN_FALLOFF = 900;

/**
 * Terrain seeds that carry the Commons LANDFORM layer (settlement plateaus,
 * rivers, terraces — the 2026-09-08 terrain wave). Personal worlds are
 * 12-hex identity seeds with their own baked grid and the forge/hive/space
 * worlds have no settlements, so the layer is gated by seed: only a seed in
 * this set evaluates settlement sites inside `terrainHeight`.
 */
export const LANDFORM_TERRAIN_SEEDS: ReadonlySet<string> = new Set(['commons-genesis']);
/** Base wavelength (m) of the climate noise fields driving biome membership.
 *  Scaled 900 -> 2700 with the 100x Commons so biome regions read ~1.3-2.7 km across.
 *  SAFE for existing builds: the city pin forces plains weights (and the plains height
 *  profile) within CITY_PIN_RADIUS, and every placed object sits inside the old +-512
 *  placement bound < the pin -- heights there do not move. */
export const BIOME_WAVELENGTH = 270000; // x100 biomes: ~135-270 km climate regions

// ─── Groups, Charters, Treasuries & Forks (P9) ───────────────────────────────────
//
// Mirror of the P9 block in `server/src/lib/constants.ts` (the SpacetimeDB module
// cannot reach across the monorepo boundary). The server is the SOLE authority for
// every charter ratification, treasury balance, spend approval, embezzlement
// penalty, and schism settlement.
//
// HARD INVARIANTS:
//   • A group Treasury is LEDGER-AUTHORITATIVE and fully transparent. Every
//     donation and spend is a P3 ledger posting; `group.treasury_sov` only
//     MIRRORS the ledger and is visible in real time to every member. No SOV
//     ever moves into or out of a Treasury off-ledger.
//   • Officers may disburse small amounts freely (≤10% of the Treasury per
//     officer per UTC day); anything larger opens a member vote that must reach
//     50% of the membership within 48h before it can execute.
//   • Embezzlement (a member recording an officer's theft) CLEAVES the officer's
//     Trustworthiness in half (the P7 score) AND demotes them to Member.
//   • A dissatisfied faction (≥33% of members) may fork. On a passing fork the
//     leaving faction walks away with a PROPORTIONAL share of the Treasury; the
//     group Hall transfers to the fork ONLY when the fork exceeds 66% of members.
//     Every schism is recorded permanently to the Memory Book (group_history).
//   • Group worlds use the P4 'guild' authorship mode and are gated by the P4
//     WorldRole permission matrix.

/** Distinct charter signatures (founder + signers) required to ratify a group. */
export const GROUP_CHARTER_SIGNATURES_REQUIRED = 5;
/** Public charter review window (µs = 24h). */
export const GROUP_REVIEW_WINDOW_MICROS = 86_400_000_000n;

/** Roles in a group (most→least privileged). Officers are a subset (see below). */
export const GROUP_ROLE = {
  Leader: 'leader',
  Treasurer: 'treasurer',
  Builder: 'builder',
  Recruiter: 'recruiter',
  Member: 'member',
} as const;
export type GroupRoleName = (typeof GROUP_ROLE)[keyof typeof GROUP_ROLE];
/** Officer roles — the ONLY roles permitted to propose/execute Treasury spends. */
export const GROUP_OFFICER_ROLES: readonly string[] = [GROUP_ROLE.Leader, GROUP_ROLE.Treasurer];

/** Canonical group lifecycle `state` values. */
export const GROUP_STATE = {
  InReview: 'in_review',
  Active: 'active',
  Dissolved: 'dissolved',
  Forked: 'forked',
} as const;
export type GroupStateName = (typeof GROUP_STATE)[keyof typeof GROUP_STATE];

/** Free-spend ceiling: ≤10% of the CURRENT Treasury per officer per UTC day (1000 bp). */
export const GROUP_SPEND_FREE_DAILY_BPS = 1_000n;
/** A vote-gated spend must reach quorum within this window (µs = 48h) or expire. */
export const GROUP_SPEND_VOTE_WINDOW_MICROS = 172_800_000_000n;
/** A vote-gated spend is approved once YES votes reach this share of members (50%). */
export const GROUP_SPEND_VOTE_PASS_BPS = 5_000n;

/** A fork is only valid if its faction is at least this share of members (33%). */
export const GROUP_FORK_INITIATE_BPS = 3_300n;
/** The Hall transfers ONLY when the fork STRICTLY EXCEEDS this share of members (66%). */
export const GROUP_FORK_HALL_BPS = 6_600n;
/** A fork vote is open for this long (µs = 48h) before the initiator resolves it. */
export const GROUP_FORK_VOTE_WINDOW_MICROS = 172_800_000_000n;

/** Embezzlement penalty: the officer's Trustworthiness is multiplied down (5000 bp = ×0.50). */
export const GROUP_EMBEZZLE_TRUST_PENALTY_BPS = 5_000n;

/** Minimum Trustworthiness (the P7 score) to sign a charter / join a group. */
export const GROUP_MIN_TRUST_TO_JOIN = 25;

